// Typed client for the Studio App versions API (/v1/studio/app-versions/*),
// read side (CV-S1). Mirrors app/models/app_versions.py. Policy fields are
// null until the backend has a policy snapshot (CV-S2 wires it in); the page
// then shows weekly active users by build only.

import { apiRequest } from "./http";

export type AppPlatform = "ios" | "android";
export type BuildStatus = "ok" | "prompted" | "blocked" | "unknown";

export interface BuildUsers {
  build: number | null; // null: sightings without a build
  users: number;
  status: BuildStatus | null;
}

export interface Floors {
  minBuild: number;
  recommendedBuild: number;
  latestStoreBuild: number;
}

export interface UserSplit {
  ok: number;
  prompted: number;
  blocked: number;
  unknown: number;
}

export interface PlatformOverview {
  platform: AppPlatform;
  wau: number;
  builds: BuildUsers[]; // newest first, unknown last
  floors: Floors | null;
  split: UserSplit | null;
}

export interface LegacyOverview {
  wau: number;
  share: number | null;
  blocked: boolean | null;
}

export interface PolicyInfo {
  templateVersion: string | null;
  publishedBy: string | null;
  publishedAt: string | null;
  backendTemplateVersion: string | null;
  inSync: boolean | null;
}

export interface AppVersionsOverview {
  generatedAt: string;
  windowStart: string;
  windowDays: number;
  totalWau: number;
  platforms: PlatformOverview[];
  legacy: LegacyOverview;
  policy: PolicyInfo | null;
}

export interface AdoptionBucket {
  key: string; // a build number, or "other"
  label: string;
}

export interface AdoptionPoint {
  day: string; // YYYY-MM-DD (UTC)
  live: boolean;
  total: number;
  users: number[]; // aligned with buckets
}

export interface AppVersionsAdoption {
  platform: AppPlatform;
  days: number;
  generatedAt: string;
  buckets: AdoptionBucket[]; // stacking order, bottom first
  points: AdoptionPoint[]; // oldest first; days without a rollup are absent
}

export function getAppVersionsOverview(): Promise<AppVersionsOverview> {
  return apiRequest<AppVersionsOverview>("/studio/app-versions/overview");
}

export function getAppVersionsAdoption(
  platform: AppPlatform,
  days = 30,
): Promise<AppVersionsAdoption> {
  const params = new URLSearchParams({ platform, days: String(days) });
  return apiRequest<AppVersionsAdoption>(`/studio/app-versions/adoption?${params}`);
}
